import { useApi } from '@hero/context';
import type {
  UseSavedLocationCreateApi,
  UseSavedLocationDeleteApi,
  UseSavedLocationUpdateApi,
  UseSavedLocationListApi,
  UseSavedLocationGetOneApi,
  UseSavedLocationResolveGoogleUrlApi,
} from './types';
import { useMutationAPI, useInfiniteQueryAPI, useQueryAPI } from '../../utils';

/** Get one saved location. */
export const useSavedLocationApi: UseSavedLocationGetOneApi = (id, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'savedLocation',
    apiFn: api.savedLocations.getById,
    singleIdQuery: id,
    query: undefined,
    apiConfig,
    isEnabled: () => !!id,
  });
};
/**
 * Create saved location. Handles cache invalidation and optional onSuccess/onError.
 * API from ApiProvider; pass apiConfig to override apiFn, invalidateQueryKeys, invalidationStrategy, etc.
 *
 * Hook API: {@link UseSavedLocationCreateApi}
 */
export const useSavedLocationCreateApi: UseSavedLocationCreateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'savedLocation',
    apiFn: api.savedLocations.create,
    apiConfig,
  });
};

/**
 * Update saved location.
 *
 * Hook API: {@link UseSavedLocationUpdateApi}
 */
export const useSavedLocationUpdateApi: UseSavedLocationUpdateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'savedLocation',
    apiFn: api.savedLocations.update,
    apiConfig,
  });
};

/**
 * List saved locations with pagination (skip/limit). Query may include `clientId`, `isOwnBranch`.
 * API from ApiProvider; pass apiConfig to override apiFn, limit, isEnabled, etc.
 *
 * Hook API: {@link UseSavedLocationListApi}
 */
export const useSavedLocationListApi: UseSavedLocationListApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'savedLocation',
    apiFn: api.savedLocations.getAll,
    query,
    apiConfig,
  });
};

/**
 * Delete saved location. Handles cache invalidation and optional onSuccess/onError.
 *
 * Hook API: {@link UseSavedLocationDeleteApi}
 */
export const useSavedLocationDeleteApi: UseSavedLocationDeleteApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'savedLocation',
    apiFn: api.savedLocations.delete,
    apiConfig,
  });
};

/**
 * Resolve a Google Maps share URL (long or short `maps.app.goo.gl`
 * link) into `{ lat, lng, name?, address?, placeId? }` for prefilling
 * the location picker. Derived data — no cache invalidation.
 *
 * Hook API: {@link UseSavedLocationResolveGoogleUrlApi}
 */
export const useSavedLocationResolveGoogleUrlApi: UseSavedLocationResolveGoogleUrlApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'savedLocation',
      apiFn: api.savedLocations.resolveGoogleUrl,
      defaultInvalidationStrategy: 'none',
      apiConfig,
    });
  };
